import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { useQuery } from '../../../hooks/useQuery'
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { PRESS_RELEASES_FILTER_CONTENT_TYPE, PressReleasesFilterContentType } from '@finance-lab/utils/types'

export type PressReleasesPaginationModel = {
    page: number
    pageSize: number
}

export const PRESS_RELAESES_FILTER_PARAMS = {
    SEARCH: 'search',
    CONTENT_TYPE: 'contentType',
    END_DATE: 'endDate',
} as const

export const validateContentType = (value: string | null): PressReleasesFilterContentType => {
    const validTypes = Object.values(PRESS_RELEASES_FILTER_CONTENT_TYPE) as PressReleasesFilterContentType[]
    return validTypes.find((k) => k === value) ?? 'all'
}

export const validateEndDate = (value: string | null): string => {
    if (!value || isNaN(new Date(value).getTime())) {
        return formatDateYYYYMMDD(new Date(), true)
    }

    return value
}

export const useFilterSettings = () => {
    const query = useQuery()

    const result = useMemo(
        () => ({
            endDate: validateEndDate(query.get(PRESS_RELAESES_FILTER_PARAMS.END_DATE)),
            contentType: validateContentType(query.get(PRESS_RELAESES_FILTER_PARAMS.CONTENT_TYPE)),
            searchValue: query.get(PRESS_RELAESES_FILTER_PARAMS.SEARCH) ?? undefined,
        }),
        [query],
    )

    return result
}

/** Reset to first page when filter changes – avoids empty lists after search on a later page. */
export const useResetPaginationOnFilterChange = (
    setPaginationModel: Dispatch<SetStateAction<PressReleasesPaginationModel>>,
    { endDate, searchValue, contentType }: ReturnType<typeof useFilterSettings>,
) => {
    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }))
    }, [contentType, endDate, searchValue, setPaginationModel])
}
